<template>
  <div class="stat-wrap">

    <!-- 統計摘要卡片 -->
    <div class="summary-cards">
      <div class="summary-card">
        <div class="sc-icon" style="background:#e8f4fd">👤</div>
        <div class="sc-body">
          <div class="sc-label">累計註冊人數</div>
          <div class="sc-value" style="color:#007bff">{{ totalUserCount.toLocaleString() }}</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="sc-icon" style="background:#eafaf1">🗂️</div>
        <div class="sc-body">
          <div class="sc-label">累計活動總數</div>
          <div class="sc-value" style="color:#28a745">{{ totalMactCount.toLocaleString() }}</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="sc-icon" style="background:#e3f8fa">✅</div>
        <div class="sc-body">
          <div class="sc-label">累計報到人數</div>
          <div class="sc-value" style="color:#17a2b8">{{ totalSignCount.toLocaleString() }}</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="sc-icon" style="background:#fff3e0">🏅</div>
        <div class="sc-body">
          <div class="sc-label">累計發放學分</div>
          <div class="sc-value" style="color:#fd7e14">{{ totalScore.toLocaleString() }}</div>
        </div>
      </div>
    </div>

    <!-- 折線圖：學分趨勢 -->
    <div class="chart-section">
      <div class="chart-title">📈 學分發放趨勢（折線圖）</div>
      <div class="chart-box">
        <svg :viewBox="`0 0 ${W} ${H}`" class="chart-svg" preserveAspectRatio="xMidYMid meet">
          <!-- 網格線 + Y 軸刻度 -->
          <g v-for="(t, i) in scoreTicks" :key="'sg'+i">
            <line :x1="PL" :y1="ySc(t)" :x2="W-PR" :y2="ySc(t)"
              stroke="#f0f0f0" stroke-width="1" stroke-dasharray="4,3"/>
            <text :x="PL-8" :y="ySc(t)+4" text-anchor="end" font-size="11" fill="#bbb">
              {{ fmtK(t) }}
            </text>
          </g>
          <!-- X 軸月份標籤 -->
          <text v-for="(d, i) in myDate" :key="'sx'+i"
            :x="xC(i)" :y="H-PB+18" text-anchor="middle" font-size="11" fill="#999">
            {{ d.date.slice(5) }}月
          </text>
          <!-- 面積填色 -->
          <polygon :points="areaPoints" fill="#17a2b8" opacity="0.07"/>
          <!-- 折線 -->
          <polyline :points="scoreLinePoints" fill="none" stroke="#17a2b8"
            stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>
          <!-- 資料點 -->
          <g v-for="(d, i) in myDate" :key="'sd'+i">
            <circle :cx="xC(i)" :cy="yScVal(d.total_score)"
              r="5" fill="#17a2b8" stroke="#fff" stroke-width="2"/>
            <title>{{ d.date }}: {{ d.total_score.toLocaleString() }} 學分</title>
          </g>
          <!-- 座標軸 -->
          <line :x1="PL" :y1="PT" :x2="PL" :y2="H-PB" stroke="#ddd" stroke-width="1"/>
          <line :x1="PL" :y1="H-PB" :x2="W-PR" :y2="H-PB" stroke="#ddd" stroke-width="1"/>
        </svg>
      </div>
    </div>

    <!-- 長條圖：報到人數 vs 註冊人數 -->
    <div class="chart-section">
      <div class="chart-title">📊 各月份人數統計（長條圖）</div>
      <div class="chart-legend">
        <span class="legend-dot" style="background:#17a2b8"></span>報到人數
        <span class="legend-dot" style="background:#fd7e14; margin-left:1.2rem"></span>註冊人數
        <span class="legend-dot" style="background:#28a745; margin-left:1.2rem"></span>活動總數
      </div>
      <div class="chart-box">
        <svg :viewBox="`0 0 ${W} ${H}`" class="chart-svg" preserveAspectRatio="xMidYMid meet">
          <!-- 網格線 + Y 軸刻度 -->
          <g v-for="(t, i) in barTicks" :key="'bg'+i">
            <line :x1="PL" :y1="yBar(t)" :x2="W-PR" :y2="yBar(t)"
              stroke="#f0f0f0" stroke-width="1" stroke-dasharray="4,3"/>
            <text :x="PL-8" :y="yBar(t)+4" text-anchor="end" font-size="11" fill="#bbb">
              {{ t.toLocaleString() }}
            </text>
          </g>
          <!-- 長條 -->
          <g v-for="(d, i) in myDate" :key="'bars'+i">
            <!-- 報到人數 -->
            <rect :x="xC(i) - barW - 1" :y="yBar(d.signdetail_count)"
              :width="barW" :height="barHt(d.signdetail_count)"
              fill="#17a2b8" rx="2" opacity="0.85">
              <title>報到人數: {{ d.signdetail_count.toLocaleString() }}</title>
            </rect>
            <!-- 註冊人數 -->
            <rect :x="xC(i) + 1" :y="yBar(d.user_count)"
              :width="barW" :height="barHt(d.user_count)"
              fill="#fd7e14" rx="2" opacity="0.85">
              <title>註冊人數: {{ d.user_count.toLocaleString() }}</title>
            </rect>
            <!-- 活動總數 (疊加在右側) -->
            <rect :x="xC(i) + barW + 3" :y="yBar(d.mact_count)"
              :width="barW" :height="barHt(d.mact_count)"
              fill="#28a745" rx="2" opacity="0.85">
              <title>活動總數: {{ d.mact_count.toLocaleString() }}</title>
            </rect>
          </g>
          <!-- X 軸月份標籤 -->
          <text v-for="(d, i) in myDate" :key="'bx'+i"
            :x="xC(i)" :y="H-PB+18" text-anchor="middle" font-size="11" fill="#999">
            {{ d.date.slice(5) }}月
          </text>
          <!-- 座標軸 -->
          <line :x1="PL" :y1="PT" :x2="PL" :y2="H-PB" stroke="#ddd" stroke-width="1"/>
          <line :x1="PL" :y1="H-PB" :x2="W-PR" :y2="H-PB" stroke="#ddd" stroke-width="1"/>
        </svg>
      </div>
    </div>

    <!-- 時間熱區圖 -->
    <div class="chart-section">
      <div class="chart-title">🔥 時間熱區圖</div>
      <div class="heat-toolbar">
        <span class="heat-tool-label">指標：</span>
        <b-button-group size="sm">
          <b-button :variant="heatMetric==='total_score'    ? 'info'    : 'outline-info'"    @click="heatMetric='total_score'">發放學分</b-button>
          <b-button :variant="heatMetric==='signdetail_count'? 'primary' : 'outline-primary'" @click="heatMetric='signdetail_count'">報到人數</b-button>
          <b-button :variant="heatMetric==='user_count'     ? 'warning' : 'outline-warning'" @click="heatMetric='user_count'">註冊人數</b-button>
          <b-button :variant="heatMetric==='mact_count'     ? 'success' : 'outline-success'" @click="heatMetric='mact_count'">活動總數</b-button>
        </b-button-group>
      </div>
      <div class="heat-grid">
        <div
          v-for="d in myDate" :key="d.date"
          class="heat-cell"
          :style="{ background: heatColor(d[heatMetric]), color: heatTextColor(d[heatMetric]) }"
          :title="`${d.date}\n${heatLabel}：${d[heatMetric].toLocaleString()}`"
        >
          <div class="heat-month">{{ d.date.slice(5) }}月</div>
          <div class="heat-val">{{ fmtK(d[heatMetric]) }}</div>
          <div class="heat-bar-wrap">
            <div class="heat-bar" :style="{ width: heatPct(d[heatMetric]) + '%' }"></div>
          </div>
        </div>
      </div>
      <div class="heat-scale">
        <span>低</span>
        <div class="heat-gradient"></div>
        <span>高</span>
      </div>
    </div>

    <!-- Top N 排行 -->
    <div class="chart-section">
      <div class="chart-title">🏆 Top N 月份排行</div>
      <div class="heat-toolbar">
        <span class="heat-tool-label">指標：</span>
        <b-button-group size="sm" class="mr-3">
          <b-button :variant="topMetric==='total_score'    ? 'info'    : 'outline-info'"    @click="topMetric='total_score'">發放學分</b-button>
          <b-button :variant="topMetric==='signdetail_count'? 'primary' : 'outline-primary'" @click="topMetric='signdetail_count'">報到人數</b-button>
          <b-button :variant="topMetric==='user_count'     ? 'warning' : 'outline-warning'" @click="topMetric='user_count'">註冊人數</b-button>
          <b-button :variant="topMetric==='mact_count'     ? 'success' : 'outline-success'" @click="topMetric='mact_count'">活動總數</b-button>
        </b-button-group>
        <span class="heat-tool-label" style="margin-left:1rem">顯示前：</span>
        <b-button-group size="sm">
          <b-button v-for="n in [3,5,10]" :key="n"
            :variant="topN===n ? 'dark' : 'outline-secondary'"
            @click="topN=n">Top {{ n }}</b-button>
          <b-button :variant="topN===999 ? 'dark' : 'outline-secondary'" @click="topN=999">全部</b-button>
        </b-button-group>
      </div>
      <div class="top-list">
        <div v-for="(d, i) in topList" :key="d.date" class="top-row">
          <div class="top-rank" :class="['rank-'+( i<3 ? i+1 : 'other')]">{{ i + 1 }}</div>
          <div class="top-date">{{ d.date }}</div>
          <div class="top-bar-wrap">
            <div class="top-bar" :style="{ width: topPct(d[topMetric]) + '%', background: topBarColor(i) }"></div>
          </div>
          <div class="top-num">{{ d[topMetric].toLocaleString() }}</div>
        </div>
      </div>
    </div>

    <!-- 明細列表 -->
    <div class="chart-section">
      <div class="chart-title-row">
        <span class="chart-title">📋 月份統計明細</span>
        <b-button variant="success" size="sm" @click="exportExcel" class="export-btn">
          ⬇️ 匯出 Excel
        </b-button>
      </div>
      <a-table
        :columns="columns"
        :data-source="myDate"
        :row-key="record => record.date"
        :pagination="false"
        bordered
        size="small"
      >
        <template slot="user_count" slot-scope="val">{{ val.toLocaleString() }}</template>
        <template slot="mact_count" slot-scope="val">{{ val.toLocaleString() }}</template>
        <template slot="signdetail_count" slot-scope="val">{{ val.toLocaleString() }}</template>
        <template slot="total_score" slot-scope="val">{{ val.toLocaleString() }}</template>
      </a-table>
    </div>

  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
const columns = [
  { title: '日期',     dataIndex: 'date',             align: 'center', width: 90 },
  { title: '註冊人數', dataIndex: 'user_count',        align: 'right',  scopedSlots: { customRender: 'user_count' } },
  { title: '活動總數', dataIndex: 'mact_count',        align: 'right',  scopedSlots: { customRender: 'mact_count' } },
  { title: '報到人數', dataIndex: 'signdetail_count',  align: 'right',  scopedSlots: { customRender: 'signdetail_count' } },
  { title: '發放學分', dataIndex: 'total_score',       align: 'right',  scopedSlots: { customRender: 'total_score' } },
]

export default {
  name: 'Actstatistics',
  props: { myDate: Array },
  data() {
    return {
      columns,
      W: 700, H: 260,
      PL: 62, PR: 20, PT: 20, PB: 38,
      heatMetric: 'total_score',
      topMetric: 'total_score',
      topN: 5,
    }
  },
  computed: {
    totalUserCount() { return this.myDate.reduce((s, d) => s + d.user_count, 0) },
    totalMactCount() { return this.myDate.reduce((s, d) => s + d.mact_count, 0) },
    totalSignCount()  { return this.myDate.reduce((s, d) => s + d.signdetail_count, 0) },
    totalScore()      { return this.myDate.reduce((s, d) => s + d.total_score, 0) },

    chartHt() { return this.H - this.PT - this.PB },

    step() {
      const n = this.myDate.length || 1
      return (this.W - this.PL - this.PR) / n
    },
    barW() { return Math.max(Math.floor(this.step * 0.28), 4) },

    /* --- 折線圖 (total_score) --- */
    maxScore() {
      const m = Math.max(...this.myDate.map(d => d.total_score), 1)
      const unit = Math.pow(10, Math.floor(Math.log10(m)) - 1) * 5
      return Math.ceil(m / unit) * unit
    },
    scoreTicks() {
      const step = this.maxScore / 5
      return Array.from({ length: 6 }, (_, i) => i * step)
    },
    scoreLinePoints() {
      return this.myDate.map((d, i) => `${this.xC(i)},${this.yScVal(d.total_score)}`).join(' ')
    },
    areaPoints() {
      if (!this.myDate.length) return ''
      const pts = this.myDate.map((d, i) => `${this.xC(i)},${this.yScVal(d.total_score)}`).join(' ')
      return `${this.xC(0)},${this.H - this.PB} ${pts} ${this.xC(this.myDate.length - 1)},${this.H - this.PB}`
    },

    /* --- 長條圖 (signdetail/user/mact) --- */
    maxBarVal() {
      const m = Math.max(...this.myDate.map(d => Math.max(d.signdetail_count, d.user_count)), 1)
      const unit = Math.pow(10, Math.floor(Math.log10(m)))
      return Math.ceil(m / unit) * unit
    },
    barTicks() {
      const step = this.maxBarVal / 4
      return Array.from({ length: 5 }, (_, i) => i * step)
    },

    /* --- 熱區圖 --- */
    heatMax() {
      return Math.max(...this.myDate.map(d => d[this.heatMetric]), 1)
    },
    heatLabel() {
      const map = { total_score: '發放學分', signdetail_count: '報到人數', user_count: '註冊人數', mact_count: '活動總數' }
      return map[this.heatMetric]
    },

    /* --- Top N --- */
    topList() {
      return [...this.myDate]
        .sort((a, b) => b[this.topMetric] - a[this.topMetric])
        .slice(0, this.topN === 999 ? undefined : this.topN)
    },
    topMax() {
      return Math.max(...this.myDate.map(d => d[this.topMetric]), 1)
    },
  },
  methods: {
    /* 熱區圖 */
    heatColor(v) {
      const pct = Math.min(v / (this.heatMax || 1), 1)
      // 從淺藍 → 深藍
      const r = Math.round(220 - pct * 180)
      const g = Math.round(235 - pct * 160)
      const b = Math.round(255 - pct * 80)
      return `rgb(${r},${g},${b})`
    },
    heatTextColor(v) {
      const pct = Math.min(v / (this.heatMax || 1), 1)
      const r = Math.round(220 - pct * 180)
      const g = Math.round(235 - pct * 160)
      const b = Math.round(255 - pct * 80)
      // 感知亮度：< 180 代表背景偏深藍，改用白色文字
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b
      return luminance < 180 ? '#fff' : '#333'
    },
    heatPct(v) {
      return Math.round((v / (this.heatMax || 1)) * 100)
    },
    /* Top N */
    topPct(v) {
      return Math.round((v / (this.topMax || 1)) * 100)
    },
    topBarColor(i) {
      if (i === 0) return '#ffd700'
      if (i === 1) return '#c0c0c0'
      if (i === 2) return '#cd7f32'
      return '#17a2b8'
    },
    xC(i) {
      return this.PL + this.step * 0.5 + i * this.step
    },
    yScVal(v) {
      return this.H - this.PB - (v / (this.maxScore || 1)) * this.chartHt
    },
    ySc(t) { return this.yScVal(t) },
    yBar(v) {
      return this.H - this.PB - (v / (this.maxBarVal || 1)) * this.chartHt
    },
    barHt(v) {
      return Math.max(this.H - this.PB - this.yBar(v), 0)
    },
    fmtK(v) {
      if (v >= 10000) return (v / 10000).toFixed(0) + '萬'
      if (v >= 1000)  return (v / 1000).toFixed(0) + 'k'
      return v
    },
    exportExcel() {
      const rows = this.myDate.map(d => ({
        '日期':     d.date,
        '註冊人數': d.user_count,
        '活動總數': d.mact_count,
        '報到人數': d.signdetail_count,
        '發放學分': d.total_score,
      }))
      const ws = XLSX.utils.json_to_sheet(rows)
      ws['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }]
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '月份統計明細')
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `統計明細_${new Date().toLocaleDateString('zh-TW').replace(/\//g, '-')}.xlsx`)
    },
  },
}
</script>

<style scoped>
.stat-wrap { padding: 0.5rem; }

/* 標題列（含匯出按鈕） */
.chart-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #17a2b8;
}
.chart-title-row .chart-title {
  margin: 0;
  padding: 0;
  border: none;
}
.export-btn {
  font-size: 0.82rem;
  padding: 0.25rem 0.8rem;
}

/* 摘要卡片 */
.summary-cards {
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}
.summary-card {
  flex: 1;
  min-width: 140px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.07);
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
}
.sc-icon {
  font-size: 1.6rem;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sc-label {
  font-size: 0.78rem;
  color: #999;
  margin-bottom: 0.2rem;
}
.sc-value {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1;
}

/* 圖表區塊 */
.chart-section {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.07);
  padding: 1.2rem 1.5rem;
  margin-bottom: 1.2rem;
}
.chart-title {
  font-size: 0.98rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #17a2b8;
}
.chart-legend {
  display: flex;
  align-items: center;
  font-size: 0.82rem;
  color: #666;
  margin-bottom: 0.6rem;
}
.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  margin-right: 5px;
}
.chart-box {
  width: 100%;
  overflow-x: auto;
}
.chart-svg {
  width: 100%;
  min-width: 320px;
  display: block;
}

/* 熱區圖 */
.heat-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1rem;
}
.heat-tool-label {
  font-size: 0.82rem;
  color: #666;
}
.heat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 0.6rem;
  margin-bottom: 0.8rem;
}
.heat-cell {
  border-radius: 8px;
  padding: 0.6rem 0.5rem 0.4rem;
  cursor: default;
  transition: transform 0.15s;
  border: 1px solid rgba(0,0,0,0.06);
}
.heat-cell:hover { transform: scale(1.05); }
.heat-month {
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.15rem;
}
.heat-val {
  font-size: 0.88rem;
  font-weight: 700;
  margin-bottom: 0.3rem;
}
.heat-bar-wrap {
  background: rgba(0,0,0,0.08);
  border-radius: 4px;
  height: 4px;
  overflow: hidden;
}
.heat-bar {
  height: 100%;
  background: #17a2b8;
  border-radius: 4px;
  transition: width 0.4s;
}
.heat-scale {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.78rem;
  color: #999;
}
.heat-gradient {
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: linear-gradient(to right, rgb(220,235,255), rgb(40,75,175));
}

/* Top N 排行 */
.top-list { display: flex; flex-direction: column; gap: 0.5rem; }
.top-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.top-rank {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;
  background: #e9ecef;
  color: #555;
}
.rank-1 { background: #ffd700; color: #7a5c00; }
.rank-2 { background: #c0c0c0; color: #444; }
.rank-3 { background: #cd7f32; color: #fff; }
.top-date {
  width: 68px;
  font-size: 0.82rem;
  color: #555;
  flex-shrink: 0;
}
.top-bar-wrap {
  flex: 1;
  background: #f0f0f0;
  border-radius: 4px;
  height: 16px;
  overflow: hidden;
}
.top-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s;
  opacity: 0.85;
}
.top-num {
  width: 70px;
  text-align: right;
  font-size: 0.85rem;
  font-weight: 600;
  color: #2c3e50;
  flex-shrink: 0;
}
</style>





